import { z } from "zod";
import { CalendarDate, CalendarDateTime } from "@internationalized/date";

/**
 * Creates a text field validator with configurable requirements.
 *
 * @param options - Configuration options
 * @param options.required - Whether the field is required
 * @param options.minLength - Minimum length (default: 1)
 * @param options.maxLength - Maximum length (optional)
 * @param options.requiredMessage - Error message when empty (default: "Required")
 * @param options.tooShortMessage - Error message when below minLength > 1
 *
 * @example
 * const nameField = createTextValidator({ required: true, maxLength: 50 });
 * const noteField = createTextValidator({ required: false, maxLength: 500 });
 * const codeField = createTextValidator({
 *   required: true,
 *   minLength: 3,
 *   tooShortMessage: "Must be at least 3 characters"
 * });
 */
export function createTextValidator(options: {
  required: boolean;
  minLength?: number;
  maxLength?: number;
  requiredMessage?: string;
  tooShortMessage?: string;
}) {
  const {
    required,
    minLength = 1,
    maxLength,
    requiredMessage = "Required",
    tooShortMessage = `String must contain at least ${minLength} character(s)`
  } = options;

  // Returning undefined keeps Zod's default message for non-string input.
  let schema = z.string({
    error: (issue) => (issue.input === undefined ? requiredMessage : undefined)
  });

  if (required && minLength) {
    schema = schema.min(minLength, {
      error: minLength > 1 ? tooShortMessage : requiredMessage
    });
  }

  if (maxLength) {
    schema = schema.max(maxLength, {
      error: `String must contain at most ${maxLength} character(s)`
    });
  }

  return required ? schema : schema.optional();
}

/**
 * Creates a CalendarDate/CalendarDateTime validator.
 *
 * @param options - Configuration options
 * @param options.required - Whether the field is required
 * @param options.errorMessage - Custom error message (default: "Required")
 *
 * @example
 * const birthdate = createCalendarDateValidator({ required: true });
 * const optionalDate = createCalendarDateValidator({ required: false });
 */
export function createCalendarDateValidator(options: {
  required: boolean;
  errorMessage?: string;
}) {
  const { required, errorMessage = "Required" } = options;

  const validator = z
    .custom<CalendarDate | CalendarDateTime | undefined>()
    .refine(
      (value) =>
        value instanceof CalendarDate || value instanceof CalendarDateTime,
      { error: errorMessage }
    );

  return required ? validator : validator.optional();
}

/** Mirrors the backend `@UserPassword` constraint on every password the API accepts. */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;

/** A function is resolved at parse time, so translations loaded after module init still apply. */
type ValidationMessage = string | (() => string);

const resolveMessage = (message: ValidationMessage): string =>
  typeof message === "function" ? message() : message;

/**
 * Creates a password validation schema (default: 12–128 characters).
 *
 * @param options - Configuration options
 * @param options.minLength - Minimum password length (default: 12)
 * @param options.maxLength - Maximum password length (default: 128)
 * @param options.requiredMessage - Message when password is missing
 * @param options.tooShortMessage - Message when password is too short
 * @param options.tooLongMessage - Message when password is too long
 *
 * @example
 * const passwordField = createPasswordSchema();
 * const customPassword = createPasswordSchema({ minLength: 16 });
 */
export function createPasswordSchema(options?: {
  minLength?: number;
  maxLength?: number;
  requiredMessage?: string;
  tooShortMessage?: ValidationMessage;
  tooLongMessage?: ValidationMessage;
}) {
  const minLength = options?.minLength ?? PASSWORD_MIN_LENGTH;
  const maxLength = options?.maxLength ?? PASSWORD_MAX_LENGTH;
  const requiredMessage = options?.requiredMessage;
  const tooShortMessage =
    options?.tooShortMessage ??
    `Password must be at least ${minLength} characters`;
  const tooLongMessage =
    options?.tooLongMessage ??
    `Password must be at most ${maxLength} characters`;

  // Returning undefined for a missing value falls through to the app's
  // registered required-field error map, unless a message is given here.
  return z
    .string({
      error: (issue) =>
        issue.input === undefined ? requiredMessage : undefined
    })
    .refine((val) => val.length >= minLength, {
      error: () => resolveMessage(tooShortMessage)
    })
    .refine((val) => val.length <= maxLength, {
      error: () => resolveMessage(tooLongMessage)
    });
}
